import ClearIcon from "@mui/icons-material/ClearRounded";
import ErrorIcon from "@mui/icons-material/ErrorOutlineRounded";
import WarningIcon from "@mui/icons-material/WarningAmberRounded";
import LowConfidenceIcon from "@mui/icons-material/WarningRounded";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import type {
  ElementSize,
  GridCellEditStopParams,
  GridCellParams,
  GridColDef,
  GridColType,
  GridPinnedColumnFields,
  GridSingleSelectColDef,
  GridSortCellParams,
  GridSortModel,
  MuiEvent,
} from "@mui/x-data-grid-premium";
import {
  GridCellEditStopReasons,
  gridDateComparator,
  gridNumberComparator,
  gridStringOrNumberComparator,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import deepEqual from "fast-deep-equal";
import type { KeyboardEvent, PropsWithChildren } from "react";
import { useCallback, useMemo, useState } from "react";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import useSingleClickGridEditing from "@entrilia/shared/hooks/useSingleClickGridEditing";
import MultilineTooltipTitle from "@entrilia/shared/components/MultilineTooltipTitle";
import SparklesIcon from "@entrilia/shared/icons/SparklesIcon";
import { makeOpaqueTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper";
import { makeNextEditableCellEdited } from "@entrilia/shared/utilities/editableDataGridHelper";
import { isDefined } from "@entrilia/shared/utilities/typeHelper";
import type { ClientDictionaries } from "../../api/types/clientTypes";
import type { Field, ObjectFieldSort, ObjectTypeToLookupReferences } from "../../api/types/objectTypes";
import useClientContext from "../../context/useClientContext";
import GridEditAutocompleteCell from "../common/grid/GridEditAutocompleteCell";
import GridEditMultiSelectCell from "../common/grid/GridEditMultiSelectCell";
import EntityValueGridCell from "./EntityValueGridCell";
import { lowConfidenceNote } from "./FieldConfidenceWarning";
import {
  fieldTypeToGridColTypeMap,
  getFieldValueLabel,
  getLazyLookupOptionsType,
  getLookupOptionAdditionalFieldValue,
  getRelatedCitationKey,
  getValueOptionsForSingleSelectField,
  isFieldConfidenceLow,
} from "./helpers";
import ObjectFieldCitationButton from "./ObjectFieldCitationButton";
import type { ValidationErrorsByRow } from "./relatedObjectsValidation";
import type { RelatedObjectRowModel } from "./relatedObjectValuesState";

export type NotesByCell = Record<string, Record<string, string>>;

export interface RelatedObjectCellDecorations {
  blockerCells: NotesByCell;
  warningCells: NotesByCell;
  autoFilledCells: NotesByCell;
}

export type RelatedColumnLayout = Pick<GridColDef, "width" | "minWidth" | "flex" | "align" | "headerAlign">;

const createSortComparator =
  (colType: GridColType | undefined) =>
  (value1: unknown, value2: unknown, params1: GridSortCellParams, params2: GridSortCellParams) => {
    // empty values are at the bottom
    if (value1 === undefined || value1 === null) {
      return 1;
    }
    if (value2 === undefined || value2 === null) {
      return -1;
    }

    // apply default sort comparator
    switch (colType) {
      case "number":
      case "boolean": {
        return gridNumberComparator(value1, value2, params1, params2);
      }
      case "date":
      case "dateTime": {
        return gridDateComparator(value1, value2, params1, params2);
      }
      case "string":
      case "singleSelect": {
        return gridStringOrNumberComparator(value1, value2, params1, params2);
      }
      default: {
        return 0;
      }
    }
  };

const actionsField = "actions";
const COLUMN_MIN_WIDTH = 80;
const ACTIONS_COLUMN_WIDTH = 50;
const PINNED_COLUMNS_PER_SIDE = 2;

const CellNotes = ({ notes, children }: PropsWithChildren<{ notes: string[] }>) =>
  notes.length > 0 ? (
    <Tooltip arrow title={<MultilineTooltipTitle lines={notes} />}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "inherit", width: 1, height: 1 }}>
        {children}
      </Box>
    </Tooltip>
  ) : (
    children
  );

const getCellOrLookupNote = (
  cellNotes: Record<string, Record<string, string>> | undefined,
  rowId: string,
  field: string
): string | undefined => {
  const [lookupFieldId = field] = field.split(".");
  return cellNotes?.[rowId]?.[field] ?? cellNotes?.[rowId]?.[lookupFieldId];
};

const getFilledLazyLookupOptionsTypes = (fields: Field[], row: RelatedObjectRowModel): string[] =>
  fields.flatMap((field) => {
    const lazyOptionsType = getLazyLookupOptionsType(field);
    return lazyOptionsType !== undefined && row[field.id] ? [lazyOptionsType] : [];
  });

const getFieldColumn = (
  fieldDefinition: Field,
  lookupOptions: ObjectTypeToLookupReferences,
  validationErrors: ValidationErrorsByRow,
  clientDictionaries: ClientDictionaries,
  allowEditing: boolean,
  onLookupOptionsNeeded: (lookupObjectTypes: string[]) => void,
  cellDecorations?: RelatedObjectCellDecorations,
  noOptionsText?: string
): GridColDef<RelatedObjectRowModel> => {
  const colType = fieldTypeToGridColTypeMap[fieldDefinition.type];

  const colDef: GridColDef<RelatedObjectRowModel> = {
    field: fieldDefinition.id,
    headerName: fieldDefinition.name,
    type: colType,
    flex: 1,
    minWidth: COLUMN_MIN_WIDTH,
    editable: allowEditing && !fieldDefinition.attributes.includes("ReadOnly"),
    sortComparator: createSortComparator(colType),
    renderCell: ({ row, field }) => {
      const fieldValue = row[field];
      const citation = row.rowState.fieldsCitations[field];
      const blocker = getCellOrLookupNote(cellDecorations?.blockerCells, row.id, field);
      const cellError = validationErrors[row.id]?.[field] ?? blocker;
      const hasValue = fieldValue !== undefined && fieldValue !== null && !(blocker && fieldValue === "");
      const autoFillNote = hasValue ? cellDecorations?.autoFilledCells[row.id]?.[field] : undefined;
      const isConfidenceLow = hasValue && !cellError && isFieldConfidenceLow(row.rowState.fieldsConfidence[field]);
      const notes = [
        cellError,
        getCellOrLookupNote(cellDecorations?.warningCells, row.id, field),
        isConfidenceLow ? lowConfidenceNote : undefined,
        autoFillNote,
      ].filter(isDefined());
      const fullValueText =
        hasValue && notes.length > 0 && (fieldDefinition.type === "Text" || fieldDefinition.type === "Lookup")
          ? getFieldValueLabel(
              [fieldDefinition],
              field,
              fieldValue,
              lookupOptions,
              row.rowState.lookupObjects[field] ?? []
            )
          : undefined;

      return (
        <CellNotes notes={fullValueText ? [fullValueText, ...notes] : notes}>
          {hasValue && (
            <EntityValueGridCell
              type={fieldDefinition.type}
              configuration={fieldDefinition.configuration}
              lookupObjectReferences={row.rowState.lookupObjects[field]}
              lookupOptionsByObjectType={lookupOptions}
              value={fieldValue}
              hideFullTextTooltip={notes.length > 0}
            />
          )}

          {autoFillNote && <SparklesIcon color="primary" />}

          {cellError && <ErrorIcon color="error" />}

          {isConfidenceLow && <LowConfidenceIcon sx={{ color: "warning.light" }} />}

          {hasValue && citation && <ObjectFieldCitationButton citationKey={getRelatedCitationKey(row.id, field)} />}
        </CellNotes>
      );
    },
  };

  const selectValueOptions = getValueOptionsForSingleSelectField(
    fieldDefinition.configuration,
    clientDictionaries,
    lookupOptions
  );

  if (fieldDefinition.type === "Select" || fieldDefinition.type === "UserDefinedOptionsSelect") {
    (colDef as GridSingleSelectColDef<RelatedObjectRowModel>).valueOptions = selectValueOptions;
  } else if (
    fieldDefinition.type === "MultiSelect" ||
    fieldDefinition.type === "UserDefinedOptionsMultiSelect" ||
    fieldDefinition.type === "MultiLookup"
  ) {
    colDef.renderEditCell = (params) => (
      <GridEditMultiSelectCell {...params} valueOptions={selectValueOptions} commitChangesOnSelect />
    );
  } else if (fieldDefinition.type === "Lookup") {
    const lazyOptionsType = getLazyLookupOptionsType(fieldDefinition);
    const areOptionsLoading = lazyOptionsType !== undefined && lookupOptions[lazyOptionsType] === undefined;

    colDef.renderEditCell = (params) => (
      <GridEditAutocompleteCell
        {...params}
        valueOptions={selectValueOptions}
        loading={areOptionsLoading}
        noOptionsText={selectValueOptions.length === 0 ? noOptionsText : undefined}
        onOpen={areOptionsLoading ? () => onLookupOptionsNeeded([lazyOptionsType]) : undefined}
        openOnFocus
        commitChangesOnSelect
      />
    );
  }

  return colDef;
};

const getLookupDisplayColumns = (
  fieldDefinition: Field,
  lookupOptions: ObjectTypeToLookupReferences,
  cellDecorations?: RelatedObjectCellDecorations
): GridColDef<RelatedObjectRowModel>[] => {
  const { configuration } = fieldDefinition;
  if (configuration?.$type !== "Lookup") {
    return [];
  }

  return (configuration.displayColumns ?? []).map(({ additionalFieldId, name }) => ({
    field: `${fieldDefinition.id}.${additionalFieldId}`,
    headerName: name,
    flex: 1,
    minWidth: COLUMN_MIN_WIDTH,
    valueGetter: (_, row) =>
      getLookupOptionAdditionalFieldValue(
        row[fieldDefinition.id],
        [...(row.rowState.lookupObjects[fieldDefinition.id] ?? []), ...(lookupOptions[configuration.objectType] ?? [])],
        additionalFieldId
      ),
    renderCell: ({ row, field, formattedValue }) => {
      const blocker = getCellOrLookupNote(cellDecorations?.blockerCells, row.id, field);
      const warning = getCellOrLookupNote(cellDecorations?.warningCells, row.id, field);

      return (
        <CellNotes notes={[blocker, warning].filter(isDefined())}>
          <Stack direction="row" spacing={0.5} useFlexGap sx={{ alignItems: "center" }}>
            {blocker ? <ErrorIcon color="error" /> : warning && <WarningIcon color="warning" />}
            {formattedValue}
          </Stack>
        </CellNotes>
      );
    },
  }));
};

const getColumns = (
  fields: Field[],
  lookupOptions: ObjectTypeToLookupReferences,
  validationErrors: ValidationErrorsByRow,
  clientDictionaries: ClientDictionaries,
  allowEditing: boolean,
  allowDeletingRows: boolean,
  onRowDelete: (row: RelatedObjectRowModel) => void,
  onLookupOptionsNeeded: (lookupObjectTypes: string[]) => void,
  cellDecorations?: RelatedObjectCellDecorations,
  columnLayouts?: Record<string, RelatedColumnLayout>,
  noOptionsTexts?: Record<string, string>
): GridColDef<RelatedObjectRowModel>[] => {
  const fieldColumns = fields.flatMap((field) => [
    getFieldColumn(
      field,
      lookupOptions,
      validationErrors,
      clientDictionaries,
      allowEditing,
      onLookupOptionsNeeded,
      cellDecorations,
      noOptionsTexts?.[field.id]
    ),
    ...getLookupDisplayColumns(field, lookupOptions, cellDecorations),
  ]);
  const columns = fieldColumns.map((column) => ({ ...column, ...columnLayouts?.[column.field] }));

  // Prevent mouse events bubbling: MUI calls getCellParams onMouseDown/onMouseUp, and the row may be deleted by that time
  if (allowDeletingRows) {
    columns.push({
      field: actionsField,
      headerName: "",
      width: ACTIONS_COLUMN_WIDTH,
      align: "right",
      renderCell: ({ row }) => (
        <Tooltip arrow title="Delete row">
          <IconButton
            sx={{ width: "100%" }}
            onMouseDown={(event) => event.stopPropagation()}
            onMouseUp={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              onRowDelete(row);
            }}
          >
            <ClearIcon color="error" />
          </IconButton>
        </Tooltip>
      ),
    });
  }

  return columns;
};

const noPinnedColumns: GridPinnedColumnFields = {};

const getPinnedColumnsWidth = (
  columns: GridColDef<RelatedObjectRowModel>[],
  { left = [], right = [] }: GridPinnedColumnFields
): number =>
  columns
    .filter((c) => left.includes(c.field) || right.includes(c.field))
    .reduce((sum, c) => sum + (c.width ?? c.minWidth ?? COLUMN_MIN_WIDTH), 0);

const getEdgeColumns = (columns: GridColDef<RelatedObjectRowModel>[]): GridPinnedColumnFields => {
  const dataFields = columns.filter((c) => c.field !== actionsField).map((c) => c.field);
  if (dataFields.length <= 2 * PINNED_COLUMNS_PER_SIDE) {
    return noPinnedColumns;
  }

  const hasActions = columns.some((c) => c.field === actionsField);

  return {
    left: dataFields.slice(0, PINNED_COLUMNS_PER_SIDE),
    right: [...dataFields.slice(-PINNED_COLUMNS_PER_SIDE), ...(hasActions ? [actionsField] : [])],
  };
};

interface Props {
  rows: RelatedObjectRowModel[];
  fields: Field[];
  lookupOptions: ObjectTypeToLookupReferences;
  sorting?: ObjectFieldSort;
  allowEditing: boolean;
  allowDeletingRows: boolean;
  onRowUpdate: (row: RelatedObjectRowModel, oldRow: RelatedObjectRowModel) => void;
  onRowDelete: (row: RelatedObjectRowModel) => void;
  loading?: boolean;
  validationErrors: ValidationErrorsByRow;
  onLookupOptionsNeeded: (lookupObjectTypes: string[]) => void;
  cellDecorations?: RelatedObjectCellDecorations;
  columnLayouts?: Record<string, RelatedColumnLayout>;
  noOptionsTexts?: Record<string, string>;
}

const RelatedObjectsGrid = ({
  rows,
  fields,
  lookupOptions,
  sorting,
  allowEditing,
  allowDeletingRows,
  onRowUpdate,
  onRowDelete,
  loading,
  validationErrors,
  onLookupOptionsNeeded,
  cellDecorations,
  columnLayouts,
  noOptionsTexts,
}: Props) => {
  const { dictionaries } = useClientContext();
  const apiRef = useGridApiRef();
  const { cellModesModel, setCellModesModel, handleCellClick } = useSingleClickGridEditing();
  const [hasRoomForScrollableColumn, setHasRoomForScrollableColumn] = useState(true);

  const columns = useMemo(
    () =>
      getColumns(
        fields,
        lookupOptions,
        validationErrors,
        dictionaries,
        allowEditing,
        allowDeletingRows,
        onRowDelete,
        onLookupOptionsNeeded,
        cellDecorations,
        columnLayouts,
        noOptionsTexts
      ),
    [
      fields,
      lookupOptions,
      validationErrors,
      dictionaries,
      allowEditing,
      allowDeletingRows,
      onRowDelete,
      onLookupOptionsNeeded,
      cellDecorations,
      columnLayouts,
      noOptionsTexts,
    ]
  );

  const getCellClassName = useCallback(
    ({ row, field }: GridCellParams<RelatedObjectRowModel>) => {
      if (getCellOrLookupNote(cellDecorations?.blockerCells, row.id, field)) {
        return "cell-with-blocker";
      }

      return getCellOrLookupNote(cellDecorations?.warningCells, row.id, field) ? "cell-with-warning" : "";
    },
    [cellDecorations]
  );

  const edgeColumns = useMemo(() => getEdgeColumns(columns), [columns]);

  const sortModel = useMemo<GridSortModel | undefined>(
    () => (sorting === undefined ? undefined : [{ field: sorting.fieldId, sort: sorting.ascending ? "asc" : "desc" }]),
    [sorting]
  );

  const handleProcessRowUpdate = useCallback(
    (updatedRow: RelatedObjectRowModel, oldRow: RelatedObjectRowModel) => {
      // Rows omit the keys of empty fields, and the grid writes an edited one back as undefined
      if (fields.some(({ id }) => !deepEqual(updatedRow[id], oldRow[id]))) {
        onLookupOptionsNeeded(getFilledLazyLookupOptionsTypes(fields, updatedRow));
        onRowUpdate(updatedRow, oldRow);
      }

      return updatedRow;
    },
    [onRowUpdate, onLookupOptionsNeeded, fields]
  );

  const handleCellEditStop = useCallback(
    (params: GridCellEditStopParams<RelatedObjectRowModel>, event: MuiEvent) => {
      if (
        params.reason === GridCellEditStopReasons.tabKeyDown ||
        params.reason === GridCellEditStopReasons.shiftTabKeyDown
      ) {
        event.defaultMuiPrevented = true;

        const direction = (event as MuiEvent<KeyboardEvent>).shiftKey ? "previous" : "next";

        const nextField = makeNextEditableCellEdited(
          direction,
          rows,
          columns,
          params,
          setCellModesModel,
          () => true,
          (r) => r.id
        );

        if (nextField !== undefined) {
          apiRef.current?.scrollToIndexes({ colIndex: columns.findIndex((c) => c.field === nextField) });
        }
      }
    },
    [apiRef, columns, rows, setCellModesModel]
  );

  const handleResize = useCallback(
    ({ width }: ElementSize) =>
      setHasRoomForScrollableColumn(width >= getPinnedColumnsWidth(columns, edgeColumns) + COLUMN_MIN_WIDTH),
    [columns, edgeColumns]
  );

  return (
    <DataGrid<RelatedObjectRowModel>
      apiRef={apiRef}
      rows={rows}
      columns={columns}
      cellModesModel={cellModesModel}
      onCellModesModelChange={setCellModesModel}
      onCellClick={handleCellClick}
      onCellEditStop={handleCellEditStop}
      processRowUpdate={handleProcessRowUpdate}
      loading={loading}
      sortModel={sortModel}
      pinnedColumns={hasRoomForScrollableColumn ? edgeColumns : noPinnedColumns}
      onResize={handleResize}
      getCellClassName={getCellClassName}
      // Otherwise a classic horizontal scrollbar makes the grid reserve a vertical one next to the pinned columns
      autoHeight
      disableColumnReorder
      disableColumnResize
      disableColumnSorting
      showColumnVerticalBorder
      showCellVerticalBorder
      noScrollableContainer
      sx={(t) => ({
        ".cell-with-validation-error": {
          color: t.palette.error.main,
        },
        ".MuiDataGrid-row .MuiDataGrid-cell.cell-with-blocker": {
          backgroundColor: makeOpaqueTintedBackgroundFromColor(t, t.palette.error.main, t.palette.background.paper),
        },
        ".MuiDataGrid-row .MuiDataGrid-cell.cell-with-warning": {
          backgroundColor: makeOpaqueTintedBackgroundFromColor(t, t.palette.warning.main, t.palette.background.paper),
        },
      })}
    />
  );
};

export default RelatedObjectsGrid;
